/**
 * Security Event Logging System
 * Monitors and logs security-related events for audit and monitoring
 */

import { sanitizeErrorMessage } from './security';
import { getSafeEnvConfig } from './envValidator';

class SecurityLogger {
  constructor() {
    this.logLevel = process.env.NODE_ENV === 'production' ? 'warn' : 'debug';
    this.maxLogEntries = 1000; // Prevent memory bloat
    this.logBuffer = [];
    this.securityEvents = new Map();
    
    // Initialize logging
    this.initializeLogging();
  }

  /**
   * Log security events with different severity levels
   */
  logSecurityEvent(level, category, message, details = {}) {
    try {
      const timestamp = new Date().toISOString();
      const logEntry = {
        timestamp,
        level,
        category,
        message,
        details: this.sanitizeLogDetails(details),
        userAgent: navigator.userAgent,
        url: window.location.href,
        sessionId: this.getCurrentSessionId()
      };

      // Add to buffer
      this.addToBuffer(logEntry);

      // Console logging based on level
      this.consoleLog(level, logEntry);

      // Track security event statistics
      this.trackSecurityEvent(category, level);

      // Send to external monitoring if configured
      this.sendToExternalMonitoring(logEntry);

    } catch (error) {
      console.error('❌ Security logging failed:', sanitizeErrorMessage(error));
    }
  }

  /**
   * Log authentication events
   */
  logAuth(action, details = {}) {
    this.logSecurityEvent('info', 'authentication', `Authentication ${action}`, {
      action,
      walletAddress: details.walletAddress?.slice(0, 8) + '...',
      provider: details.provider,
      success: details.success,
      ...details
    });
  }

  /**
   * Log authorization events
   */
  logAuthorization(action, details = {}) {
    this.logSecurityEvent('info', 'authorization', `Authorization ${action}`, {
      action,
      resource: details.resource,
      permission: details.permission,
      granted: details.granted,
      ...details
    });
  }

  /**
   * Log input validation events
   */
  logInputValidation(type, details = {}) {
    this.logSecurityEvent('warn', 'input_validation', `Input validation ${type}`, {
      inputType: details.inputType,
      validationResult: details.validationResult,
      sanitized: details.sanitized,
      ...details
    });
  }

  /**
   * Log rate limiting events
   */
  logRateLimit(action, details = {}) {
    this.logSecurityEvent('warn', 'rate_limiting', `Rate limit ${action}`, {
      action,
      identifier: details.identifier?.slice(0, 8) + '...',
      limit: details.limit,
      current: details.current,
      blocked: details.blocked,
      ...details
    });
  }

  /**
   * Log session events
   */
  logSession(action, details = {}) {
    this.logSecurityEvent('info', 'session', `Session ${action}`, {
      action,
      sessionId: details.sessionId,
      duration: details.duration,
      reason: details.reason,
      ...details
    });
  }

  /**
   * Log security violations
   */
  logSecurityViolation(type, details = {}) {
    this.logSecurityEvent('error', 'security_violation', `Security violation: ${type}`, {
      violationType: type,
      severity: details.severity || 'high',
      blocked: details.blocked,
      ...details
    });
  }

  /**
   * Log blockchain/wallet events
   */
  logBlockchain(action, details = {}) {
    this.logSecurityEvent('info', 'blockchain', `Blockchain ${action}`, {
      action,
      network: details.network,
      programId: details.programId,
      transactionId: details.transactionId,
      success: details.success,
      ...details
    });
  }

  /**
   * Sanitize log details to prevent information disclosure
   */
  sanitizeLogDetails(details) {
    const sanitized = { ...details };
    
    // Remove or mask sensitive fields
    const sensitiveFields = ['privateKey', 'mnemonic', 'seed', 'password', 'token', 'secret'];
    
    sensitiveFields.forEach(field => {
      if (sanitized[field]) {
        sanitized[field] = '[REDACTED]';
      }
    });

    // Truncate long wallet addresses
    if (sanitized.walletAddress && sanitized.walletAddress.length > 16) {
      sanitized.walletAddress = sanitized.walletAddress.slice(0, 8) + '...';
    }

    return sanitized;
  }

  /**
   * Add log entry to buffer with size management
   */
  addToBuffer(logEntry) {
    this.logBuffer.push(logEntry);
    
    // Maintain buffer size
    if (this.logBuffer.length > this.maxLogEntries) {
      this.logBuffer.shift(); // Remove oldest entry
    }
  }

  /**
   * Console logging with appropriate levels
   */
  consoleLog(level, logEntry) {
    const message = `🔒 [${level.toUpperCase()}] ${logEntry.category}: ${logEntry.message}`;
    
    switch (level) {
      case 'error':
        console.error(message, logEntry.details);
        break;
      case 'warn':
        console.warn(message, logEntry.details);
        break;
      case 'info':
        console.info(message, logEntry.details);
        break;
      case 'debug':
        if (process.env.NODE_ENV === 'development') {
          console.log(message, logEntry.details);
        }
        break;
      default:
        console.log(message, logEntry.details);
    }
  }

  /**
   * Track security event statistics
   */
  trackSecurityEvent(category, level) {
    const key = `${category}_${level}`;
    const current = this.securityEvents.get(key) || 0;
    this.securityEvents.set(key, current + 1);
  }

  /**
   * Get current session ID for logging
   */
  getCurrentSessionId() {
    try {
      // Try to get session ID from session manager if available
      if (window.sessionManager) {
        const stats = window.sessionManager.getSessionStats();
        return stats?.sessionId || 'unknown';
      }
      return 'no-session';
    } catch (error) {
      return 'error-getting-session';
    }
  }

  /**
   * Send logs to external monitoring service (if configured)
   */
  sendToExternalMonitoring(logEntry) {
    try {
      // Only send high-priority events to external monitoring
      if (['error', 'warn'].includes(logEntry.level)) {
        // This would integrate with services like Sentry, LogRocket, etc.
        // For now, just store for potential batch sending
        if (process.env.REACT_APP_ENABLE_SENTRY === 'true') {
          // Sentry integration would go here
          console.log('📤 Would send to external monitoring:', logEntry.category);
        }
      }
    } catch (error) {
      console.error('❌ Failed to send to external monitoring:', sanitizeErrorMessage(error));
    }
  }

  /**
   * Get security statistics for monitoring dashboard
   */
  getSecurityStats() {
    return {
      totalEvents: this.logBuffer.length,
      eventsByCategory: Object.fromEntries(this.securityEvents),
      recentEvents: this.logBuffer.slice(-10), // Last 10 events
      bufferSize: this.logBuffer.length,
      maxBufferSize: this.maxLogEntries
    };
  }

  /**
   * Export logs for analysis (development only)
   */
  exportLogs() {
    if (process.env.NODE_ENV !== 'development') {
      console.warn('⚠️ Log export only available in development mode');
      return null;
    }

    return {
      logs: this.logBuffer,
      stats: this.getSecurityStats(),
      environment: getSafeEnvConfig(),
      exportedAt: new Date().toISOString()
    };
  }

  /**
   * Initialize logging system
   */
  initializeLogging() {
    // Log system initialization
    this.logSecurityEvent('info', 'system', 'Security logging initialized', {
      environment: process.env.NODE_ENV,
      logLevel: this.logLevel,
      maxLogEntries: this.maxLogEntries
    });

    // Set up global error handler
    window.addEventListener('error', (event) => {
      this.logSecurityEvent('error', 'javascript_error', 'Uncaught JavaScript error', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack
      });
    });

    // Set up unhandled promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      this.logSecurityEvent('error', 'promise_rejection', 'Unhandled promise rejection', {
        reason: sanitizeErrorMessage(event.reason)
      });
    });

    console.log('✅ Security logging system initialized');
  }
}

// Create singleton instance
const securityLogger = new SecurityLogger();

// Make available globally for debugging
if (process.env.NODE_ENV === 'development') {
  window.securityLogger = securityLogger;
}

export default securityLogger;
export { SecurityLogger };
